import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import type { IqServiceInventoryItem } from '@/types/iq-service-inventory';
import { iqServiceStatusBadgeVariant } from './iq-service-inventory-status';

interface IqServiceInventoryDetailDialogProps {
  item: IqServiceInventoryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Read-only detail view of a single inventory entry (UC-1). */
export function IqServiceInventoryDetailDialog({
  item,
  open,
  onOpenChange,
}: IqServiceInventoryDetailDialogProps) {
  const { t } = useTranslation('iq-service-inventory');
  const navigate = useNavigate();
  if (!item) return null;

  const dash = t('detail.notSet');
  const usageCount = item.fallbackUsageCount ?? 0;
  const usedBy = item.usedBy ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('detail.title')}</DialogTitle>
          <DialogDescription className="font-mono break-all">{item.iqServiceKey}</DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">{t('detail.fields.status')}</dt>
          <dd>
            <Badge variant={iqServiceStatusBadgeVariant(item.status)}>{t(`status.${item.status}`)}</Badge>
          </dd>

          {/* Spec 347 §6: BFF-derived usage (Dimension a) — entry count + clickable services */}
          <dt className="text-muted-foreground">{t('detail.fields.usage')}</dt>
          <dd>
            {item.usageUnavailable || item.usageTruncated ? (
              <span className="text-muted-foreground">{t('usage.usageUnknown')}</span>
            ) : usageCount === 0 ? (
              <span className="text-muted-foreground">{t('usage.notUsed')}</span>
            ) : (
              <div className="space-y-1">
                {/* Spec 352 §6: Drill-down in die gefilterte /services-Sicht;
                    `&merchant=` mitgeben (das Inventar kennt den Merchant). */}
                <Link
                  to={`/services?gw=${encodeURIComponent(item.iqServiceKey)}&merchant=${encodeURIComponent(item.merchantId)}`}
                  className="hover:text-primary hover:underline"
                  onClick={() => onOpenChange(false)}
                >
                  {t('usage.inEntries', { count: usageCount })}
                </Link>
                <ul className="space-y-0.5">
                  {usedBy.map((u) => (
                    <li key={u.serviceId}>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenChange(false);
                          navigate(`/services/${u.serviceId}/routing`);
                        }}
                        className="text-left text-xs text-primary hover:underline"
                      >
                        {u.displayName}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </dd>

          <dt className="text-muted-foreground">{t('detail.fields.flowType')}</dt>
          <dd>{item.flowType ? t(`flowType.${item.flowType}`) : dash}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.bookingType')}</dt>
          <dd>{item.bookingType ? t(`bookingType.${item.bookingType}`) : dash}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.displayName')}</dt>
          <dd>{item.displayName || dash}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.description')}</dt>
          <dd className="whitespace-pre-wrap">{item.description || dash}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.internetqEmailRef')}</dt>
          <dd className="break-all">{item.internetqEmailRef || dash}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.registeredAt')}</dt>
          <dd>{item.registeredAt}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.registeredBy')}</dt>
          <dd className="font-mono break-all">{item.registeredBy}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.updatedAt')}</dt>
          <dd>{item.updatedAt}</dd>

          <dt className="text-muted-foreground">{t('detail.fields.updatedBy')}</dt>
          <dd className="font-mono break-all">{item.updatedBy || dash}</dd>
        </dl>
      </DialogContent>
    </Dialog>
  );
}
